import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Form } from "react-bootstrap";
import { IconDownload } from "../../icons/Icon";
import SmuBadge from "../../utils/smu-badge/SmuBadge";
import SmuSearch from "../../utils/smu-search/SmuSearch";
import { prepareText, pascalCase, resolveUserTienda } from "../../helpers";
import { COLOR_PRIMARY, COLOR_TEXT } from "../../constants/colors";
import SmuTable from "../../utils/smu-table/SmuTable";
import { StoreContext } from "../../store/StoreContext";
import { getStores } from "../../api/stores";
import { TfiReload } from "react-icons/tfi";
import SmuButton from "../../utils/smu-button/SmuButton";
import { IconEye } from "../../icons/Icon";
import { SMU_FILTER, SMU_SELECTED_MENU, SMU_PRESERVE_PROMOTION_FILTERS } from "../../constants";
import { downloadFaldonXlsx, downloadFaldonXlsxByPromotionIdWithCopiesAndVersion, getLatestDownloadFaldonStatus } from "../../api/files";
import { toast } from "../../helpers/smu-alerts/smu-lerts";

const rowsStyle = {
  "&:hover": {
    backgroundColor: "#ededed",
  },
  cursor: "pointer",
};

const getTwoDaysFromToday = () => {
  const target = new Date();
  target.setDate(target.getDate() + 2);
  const targetYear = target.getFullYear();
  const targetMonth = String(target.getMonth() + 1).padStart(2, '0');
  const targetDay = String(target.getDate()).padStart(2, '0');
  return `${targetYear}-${targetMonth}-${targetDay}`;
};

const PromotionsTable = ({
  promotions,
  clickItem,
  setPromotions,
  selectedPromotions,
  download,
  loading,
  handleApplyFilters,
  setPromotionsList
}) => {
  const navigate = useNavigate();
  const filter = JSON.parse(localStorage.getItem(SMU_FILTER));
  // Filter states
  const [p_categoria, setP_categoria] = useState("");
  const [p_ean, setP_ean] = useState(filter?.ean ? filter.ean : "");
  const [p_nombre_producto, setP_nombre_producto] = useState("");
  const [p_seccion, setP_seccion] = useState("");
  const [p_linea, setP_linea] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  // New implementation
  const [tipoPagina, setTipoPagina] = useState({value: filter?.formato_impresion || "cuarto-pagina"});
  const [excel, setExcel] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingPromotion, setDownloadingPromotion] = useState(null);
  const [loadingPercent, setLoadingPercent] = useState(0);
  const [readyToFilter, setReadyToFilter] = useState(false);
  const [selectedMenuTitle, setSelectedMenuTitle] = useState("");

  // Visualizar como tienda (store view) - local only
  const { isAdmin, loggedUser } = useContext(StoreContext);
  const [visualizarTienda, setVisualizarTienda] = useState(
    isAdmin ? null : resolveUserTienda(isAdmin, loggedUser) == -1 ? null : resolveUserTienda(isAdmin, loggedUser)
  );
  const [allStores, setAllStores] = useState([]);
  const [adminStoreSearch, setAdminStoreSearch] = useState("");
  const [isAdminStoreDropdownOpen, setIsAdminStoreDropdownOpen] = useState(false);
  const adminDropdownRef = useRef(null);
  const adminSelectRef = useRef(null);
  const hasClearedFiltersOnMount = useRef(false);

  // Filtrar Promociones por ACTUALIZACION
  const [onlyUpdated, setOnlyUpdated] = useState(false);

  const filteredStores = useMemo(() => {
    const list = Array.isArray(allStores) ? allStores : [];
    const query = (adminStoreSearch || "").trim().toLowerCase();
    if (!query) return list;
    return list.filter((store) => {
      const code = String(store?.codLocal ?? store?.codigoLocal ?? store?.codigo ?? store?.id ?? "");
      const label = /^\d+$/.test(code) ? parseInt(code, 10).toString().padStart(4, '0') : code;
      const desc = String(store?.descripcion ?? store?.description ?? "").toLowerCase();
      return label.toLowerCase().includes(query) || desc.includes(query);
    });
  }, [allStores, adminStoreSearch]);

  const getAdminSelectedStoreDisplay = () => {
    if (!visualizarTienda) return 'Todas';
    const code = String(visualizarTienda);
    return /^\d+$/.test(code) ? parseInt(code, 10).toString().padStart(4, '0') : code;
  };

  const downloadPromotion = async (row) => {
    setDownloading(true);
    setDownloadingPromotion({id: row.ID_PROMO, negocio: row.NEGOCIO, formatoImpresion: tipoPagina.value, nombreCampana: row.NOMBRE_CAMPANA});
    setLoadingPercent(0);
    const username = loggedUser?.username;
    const tienda = visualizarTienda ? visualizarTienda : resolveUserTienda(isAdmin, loggedUser);
    const negocio = row.NEGOCIO;
    const formatoImpresion = tipoPagina.value;
    const nombreCampana = row.NOMBRE_CAMPANA;

    const statusToMax = { FILTERING: 40, RPA_VIRAL: 94, SUCCESS: 100, FAILURE: 100 };
    const statusBase = { FILTERING: 1, RPA_VIRAL: 41, SUCCESS: 95, FAILURE: 95 };

    let progress = 0;
    let maxDuringDownload = statusToMax.FILTERING;
    const intervalDelay = 150;
    let progressTimeoutId = null;
    let pollingIntervalId = null;

    const runProgressCycle = () => {
      const randomDelay = Math.floor(Math.random() * 350);
      const intervalStep = Math.floor(Math.random() * 3);
      progress = Math.min(progress + intervalStep, maxDuringDownload);
      setLoadingPercent(progress);
      progressTimeoutId = setTimeout(runProgressCycle, intervalDelay + randomDelay);
    };

    const clearTimers = () => {
      if (progressTimeoutId) {
        clearTimeout(progressTimeoutId);
      }
      if (pollingIntervalId) {
        clearInterval(pollingIntervalId);
      }
    };

    runProgressCycle();

    pollingIntervalId = setInterval(async () => {
      try {
        if (progress > statusToMax.FILTERING) return;
        const downloadId = row.ID_PROMO
          + (formatoImpresion ? ("::" + formatoImpresion.toLowerCase()) : "") 
          + (negocio ? ("::" + negocio.toUpperCase()) : "") 
          + (nombreCampana ? ("::" + nombreCampana.toLowerCase()) : "");
        const statusResp = await getLatestDownloadFaldonStatus(downloadId, username);
        if (!statusResp || !statusResp.REQUEST_STATUS) return;
        const newMax = statusToMax[statusResp.REQUEST_STATUS] ?? maxDuringDownload;
        if (newMax > maxDuringDownload) {
          maxDuringDownload = newMax;
          progress = statusBase[statusResp.REQUEST_STATUS] ?? progress;
          setLoadingPercent(progress);
        }
      } catch (err) {
        console.warn("status fetch failed.");
      }
    }, 7000);

    try {
      // Use two days from today for start_date_to, keep end_date_from as today
      const targetDate = getTwoDaysFromToday();

      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const todayDate = `${year}-${month}-${day}`;
      const params = {
        tienda,
        username,
        negocio,
        formatoImpresion,
        nombreCampana,
        start_date_to: targetDate,
        end_date_from: todayDate
      }
      if (p_ean) params.eans = [p_ean];
      if (onlyUpdated) params.actualizaciones = true;
      const response = await downloadFaldonXlsx(row.ID_PROMO, params);

      setLoadingPercent(97);

      if (response.status === 204) {
        toast({ icon: "info", title: "No hay eans que coincidan para el faldón" });
        return false;
      }

      let downloadFilename = `${row.ID_PROMO}.pdf`;
      const disposition = response.headers?.["content-disposition"] || response.headers?.["Content-Disposition"];
      if (disposition) {
        const match = disposition.match(/filename=\"?([^\";]+)\"?/);
        if (match && match[1]) {
          downloadFilename = match[1];
        }
      }

      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", downloadFilename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      setLoadingPercent(100);
      return true;
    } catch (err) {
      toast({ icon: "error", title: `Error descargando ${row.ID_PROMO}` });
      return false;
    } finally {
      clearTimers();
      setDownloading(false);
      setDownloadingPromotion(null);
    }
  };

  // Clear filter states when component mounts (to sync with cleared localStorage from parent)
  useEffect(() => {
    if (hasClearedFiltersOnMount.current) return;
    hasClearedFiltersOnMount.current = true;
    // Clear all filter states except formato_impresion (which is preserved in localStorage)
    setP_categoria("");
    setP_ean("");
    setP_nombre_producto("");
    setP_seccion("");
    setP_linea("");
    // Reset visualizarTienda to default
    if (isAdmin) {
      setVisualizarTienda(null);
    } else {
      const defaultTienda = resolveUserTienda(isAdmin, loggedUser) == -1 ? null : resolveUserTienda(isAdmin, loggedUser);
      setVisualizarTienda(defaultTienda);
    }
    setReadyToFilter(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isAdmin) {
      setAllStores([]);
      return;
    }
    getStores().then(({ data }) => {
      setAllStores(data || []);
    }).catch(() => {
      setAllStores([]);
    });
  }, [isAdmin]);

    useEffect(() => {
    // clean filters only if the formato_impresion is different
    var filter = JSON.parse(localStorage.getItem(SMU_FILTER));
    if(filter?.formato_impresion !== tipoPagina.value) {
      filter.formato_impresion = tipoPagina.value;
      filter.ean = "";
      localStorage.setItem(SMU_FILTER, JSON.stringify(filter));
      setP_categoria("");
      setP_ean("");
      setP_nombre_producto("");
      setP_seccion("");
      setP_linea("");
      handleFilter();
    }
  }, [tipoPagina]);

  useEffect(() => {
    if (!readyToFilter) return;
    handleFilter();
  }, [readyToFilter, handleFilter]);

  const handleAddPromotion = (event) => {
    const _code = event.target.value;
    const _check = event.target.checked;

    setPromotions((prevPromotions) => {
      if (!_check) return prevPromotions.filter((p) => p.ID_PROMO !== _code);
      const _promo = promotions.find((p) => p.ID_PROMO === _code);
      return [...prevPromotions, _promo];
    });
  };

  const handleAddAll = (event) => {
    if (event.target.checked) setPromotions(promotions.map((p) => p));
    else setPromotions([]);
  };

  const handleDownloadPromotion = async (row) => {
    if (downloading) return;
    const wasDownloaded = await downloadPromotion(row);
    if (!wasDownloaded) return;
    const updatedPromotions = promotions.map((p) => {
      if (p.ID_PROMO === row.ID_PROMO && p.NEGOCIO === row.NEGOCIO) {
        const currentViews = parseInt(p.VIEWS, 10);
        const nextViews = isNaN(currentViews) ? 1 : currentViews + 1;
        return { ...p, DOWNLOADED: true, VIEWS: nextViews };
      }
      return p;
    });
    setPromotionsList(updatedPromotions);
  };

  const handleFilter = useCallback(() => {
    const filters = {
      categoriaPromo: p_categoria,
      ean: [p_ean],
      nombreProducto: p_nombre_producto,
      seccion: p_seccion,
      linea: p_linea,
      formato_impresion: tipoPagina.value,
      tienda: visualizarTienda,
    };
    const filtro = JSON.parse(localStorage.getItem(SMU_FILTER));
    if (p_ean) {
      filtro.ean = p_ean;
    }
    localStorage.setItem(SMU_FILTER, JSON.stringify(filtro));
    handleApplyFilters(filters);
  }, [
    p_categoria,
    p_ean,
    p_nombre_producto,
    p_seccion,
    p_linea,
    tipoPagina.value,
    visualizarTienda,
    handleApplyFilters,
  ]);

  const syncSelectedMenuTitle = useCallback(() => {
    try {
      const raw = localStorage.getItem(SMU_SELECTED_MENU);
      if (!raw) {
        setSelectedMenuTitle("");
        return;
      }
      const parsed = JSON.parse(raw);
      const menuTitle = parsed?.title;
      setSelectedMenuTitle(menuTitle ? String(menuTitle).toUpperCase() : "");
    } catch (err) {
      setSelectedMenuTitle("");
    }
  }, []);

  useEffect(() => {
    syncSelectedMenuTitle();
  }, [syncSelectedMenuTitle]);

  // Re-run filters when SMU_SELECTED_MENU (category_menu) is changed from the sidebar
  useEffect(() => {
    const onSelectedMenuChanged = () => {
      syncSelectedMenuTitle();
      handleFilter();
    };

    window.addEventListener("smu-selected-menu-changed", onSelectedMenuChanged);
    return () => {
      window.removeEventListener("smu-selected-menu-changed", onSelectedMenuChanged);
    };
  }, [handleFilter, syncSelectedMenuTitle]);

  const columns = useMemo(
    () => [
      /*{
        name: "Cod. Promo",
        selector: (row) => row.ID_PROMO,
        cell: (row) => (
          <div className="cell text-truncate" onClick={() => clickItem(row)}>
            {row.ID_PROMO}
          </div>
        ),
        sortable: true,
      },
      {
        selector: (row) => row.ESTATUS,
        cell: (row) => (
          <div className="cell text-truncate">
            {new Date(row.DOWNLOADED) < new Date(row.FECHA_HORA_CARGA) ? <TfiReload /> : ""}
          </div>
        ),
        width: "30px",
      },*/
      {
        name: "Negocio",
        selector: (row) => row.NEGOCIO,
        cell: (row) => (
          <div className="cell text-truncate">
            {row.NEGOCIO}
          </div>
        ),
        sortable: true,
        width: "100px",
      },
      {
        name: "Descripción Promoción",
        selector: (row) => row.DESC_PROMO,
        cell: (row) => (
          <div className="cell text-truncate" onClick={() => clickItem(row)} title={row.ID_PROMO}>
            {pascalCase(row.DESC_PROMO)}
          </div>
        ),
        sortable: true,
      },
      {
        name: "Papel Faldon",
        selector: (row) => row.NOMBRE_CAMPANA,
        cell: (row) => (
          <div className="cell text-truncate" onClick={() => clickItem(row)} title={row.NOMBRE_CAMPANA}>
            {row.NOMBRE_CAMPANA}
          </div>
        ),
        sortable: true,
        maxWidth: "220px",
        minWidth: "190px",
      },
      {
        id: "FECHA_INICIO",
        name: "Inicio",
        selector: (row) => row.INICIO_PROMO,
        cell: (row) => (
          <div className="cell text-truncate" onClick={() => clickItem(row)}>
            {row.INICIO_PROMO ? new Date(row.INICIO_PROMO).toLocaleDateString('es-ES', { timeZone: 'UTC', day: '2-digit', month: '2-digit', year: 'numeric' }) : ''}
          </div>
        ),
        sortable: true,
        width: "100px",
      },
      {
        name: "Término",
        selector: (row) => row.FIN_PROMO,
        cell: (row) => (
          <div className="cell text-truncate" onClick={() => clickItem(row)}>
            {row.FIN_PROMO ? new Date(row.FIN_PROMO).toLocaleDateString('es-ES', { timeZone: 'UTC', day: '2-digit', month: '2-digit', year: 'numeric' }) : ''}
          </div>
        ),
        sortable: true,
        width: "100px",
      },
      {
        name: "Actualización",
        selector: (row) => row.FECHA_HORA_CARGA,
        cell: (row) => (
          <div className="cell text-truncate" onClick={() => clickItem(row)}>
            {row.FECHA_HORA_CARGA != null ? new Date(row.FECHA_HORA_CARGA).toLocaleDateString('es-ES', {timeZone: 'UTC', day: '2-digit', month: '2-digit', year: 'numeric'}) : "-"}
          </div>
        ),
        sortable: true,
        width: "100px",
      },
      {
        name: "Vistas",
        selector: (row) => row.VIEWS,
        cell: (row) => (
          <div className="cell text-truncate">
            {row.VIEWS}
          </div>
        ),
        sortable: true,
        width: "70px",
      },
      {
        name: "Ver",
        selector: (row) => row.ID_PROMO,
        cell: (row) => (
          <div className="cell text-truncate" title={row.DOWNLOADED == false ? "Debe descargar el faldon completo ver sus artículos." : ""}>
            <SmuButton
              _className="mx-auto"
              control={() => {
                // Set flag to preserve filters when navigating to ProductPromotionsTable
                localStorage.setItem(SMU_PRESERVE_PROMOTION_FILTERS, "true");
                navigate(`/promotional-portal/portal/promotion/detail/${row.ID_PROMO}`, {
                  state: {
                    promoData: row,
                    selectedMenu: localStorage.getItem(SMU_SELECTED_MENU),
                  },
                });
              }}
              title="Ver artículos asociados a la promoción"
              disabled={row.DOWNLOADED == false || row.FECHA_HORA_CARGA == null}
            >
              <IconEye color="#33383F" />
            </SmuButton>
          </div>
        ),
        width: "70px",
      },
      {
        name: "Status",
        selector: (row) => row.ESTATUS,
        cell: (row) => {
          if (row.FECHA_HORA_CARGA == null) { return <div className="cell text-truncate" onClick={() => clickItem(row)}>Sin faldones</div>; }
          if (row.ACTUALIZACION === 1)
            return <div className="cell text-truncate" onClick={() => clickItem(row)}>Actualizado</div>;
          return (
          <div className="cell text-truncate" onClick={() => clickItem(row)}>
            {row.IS_VALID == 1 ? "Vigente" : "No vigente"}
          </div>
        )},
        sortable: true,
        grow: 0,
        width: "100px",
      },
      {
        name: "Excel",
        selector: (row) => row.CATALOGO,
        cell: (row) => (
          <div className="cell text-truncate d-flex justify-content-end gap-2">
            <Form.Check
              type="checkbox"
              id={`checkbox-${row.CATALOGO}`}
              value={row.ID_PROMO}
              onChange={handleAddPromotion}
              checked={selectedPromotions
                .map((p) => p.ID_PROMO)
                .includes(row.ID_PROMO)}
            />
          </div>
        ),
        width: "60px",
      },
      {
        name: "Descargar",
        selector: (row) => row.ID_PROMO,
        cell: (row) => (
          <div className="cell text-truncate" style={{ marginRight: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
            <SmuButton
              _className="mx-auto"
              control={() => handleDownloadPromotion(row)}
              highlightPin={row.DOWNLOADED == false}
              loading={downloadingPromotion?.id === row.ID_PROMO && downloadingPromotion?.negocio === row.NEGOCIO}
              loadingPercent={downloadingPromotion?.id === row.ID_PROMO && downloadingPromotion?.negocio === row.NEGOCIO ? loadingPercent : null}
              loadingPercentPosition={"top"}
              disabled={downloading || row.FECHA_HORA_CARGA == null}
              title="Descargar faldones asociados a la promoción"
            >
              <IconDownload color="#33383F" />
            </SmuButton>
          </div>
        ),
        width: "90px",
      }
    ],
    [selectedPromotions, promotions, downloading, downloadingPromotion, loadingPercent, onlyUpdated]

  );

  const [search, setSearch] = useState("");
  const [filteredPromotions, setFilteredPromotions] = useState([]);

  useEffect(() => {
    let _promotions = promotions;

    if (onlyUpdated) {
      _promotions = _promotions.filter((p) => p.ACTUALIZACION === 1);
    }
    if (search) {
      _promotions = _promotions.filter((p) => {
        const _search = prepareText(search.trim());
        const _code = prepareText(p.ID_PROMO);
        const _desc = prepareText(p.DESCRIPCION_PROMO);
        const _start = prepareText(p.FECHA_INICIO);
        const _end = prepareText(p.FECHA_TERMINO);
        const _status = prepareText(p.ESTATUS);
        return (
          _code.includes(_search) ||
          _desc.includes(_search) ||
          _start.includes(_search) ||
          _end.includes(_search) ||
          _status.includes(_search)
        );
      });
    }

    setFilteredPromotions(_promotions);
  }, [search, promotions, onlyUpdated]);

  return (
    <Card className="smu-card">
      <Card.Header className="d-flex flex-column py-4">
        <div className="d-flex align-items-center mb-3">
          <SmuBadge color="#B1E5FC" />
          <span className="fs-5 ms-3">
            {`Promociones Unimarc${selectedMenuTitle ? ` | ${selectedMenuTitle}` : ""}`}
          </span>
          <Form.Group className="ms-3">
            <Form.Select
              value={tipoPagina.value}
              onChange={(e) => setTipoPagina({value: e.target.value})}
            >
              <option value="cuarto-pagina">Cuarto de página</option>
              <option value="pagina-completa">Página completa</option>
              <option value="pagina-completa-horizontal">Página completa horizontal</option>
              <option value="media-pagina">Media página</option>
            </Form.Select>
          </Form.Group>
          {/* <Form.Group className="me-1">
            <Form.Switch
              checked={excel}
              onChange={(e) => setExcel(e.target.checked)}
            />
          </Form.Group> */}
        </div>
        
        {/* Filter Menu */}
        <div className="row g-3 align-items-end">
          {/* Toggle button for filters */}
          <div className="col-12 mb-3">
            <button
              type="button"
              className="btn btn-link p-0 text-decoration-none d-flex align-items-center"
              onClick={() => setShowFilters(!showFilters)}
              style={{
                color: '#6c757d',
                fontSize: '14px',
                border: 'none',
                background: 'none'
              }}
            >
              <span className="me-2">
                {showFilters ? 'v' : '>'}
              </span>
              {showFilters ? 'Ocultar filtros' : 'Mostrar filtros'}
            </button>
          </div>

          {/* Filters section - conditionally rendered */}
          {showFilters && (
            <>
          <div className="col-md-2">
            <Form.Group>
              <Form.Label className="text-muted small mb-1">Categoría</Form.Label>
              <Form.Control
                type="text"
                placeholder="Ingrese categoría"
                value={p_categoria}
                onChange={(e) => setP_categoria(e.target.value)}
                style={{
                  backgroundColor: '#f4f4f4',
                  borderRadius: '12px',
                  border: 'none',
                  height: '44px'
                }}
              />
            </Form.Group>
          </div>
          
          <div className="col-md-2">
            <Form.Group>
              <Form.Label className="text-muted small mb-1">EAN</Form.Label>
              <Form.Control
                type="text"
                placeholder="Ingrese EAN"
                value={p_ean}
                onChange={(e) => setP_ean(e.target.value)}
                style={{
                  backgroundColor: '#f4f4f4',
                  borderRadius: '12px',
                  border: 'none',
                  height: '44px'
                }}
              />
            </Form.Group>
          </div>
          
          <div className="col-md-2">
            <Form.Group>
              <Form.Label className="text-muted small mb-1">Nombre Producto</Form.Label>
              <Form.Control
                type="text"
                placeholder="Ingrese nombre"
                value={p_nombre_producto}
                onChange={(e) => setP_nombre_producto(e.target.value)}
                style={{
                  backgroundColor: '#f4f4f4',
                  borderRadius: '12px',
                  border: 'none',
                  height: '44px'
                }}
              />
            </Form.Group>
          </div>
          
          <div className="col-md-2">
            <Form.Group>
              <Form.Label className="text-muted small mb-1">Sección</Form.Label>
              <Form.Control
                type="text"
                placeholder="Ingrese sección"
                value={p_seccion}
                onChange={(e) => setP_seccion(e.target.value)}
                style={{
                  backgroundColor: '#f4f4f4',
                  borderRadius: '12px',
                  border: 'none',
                  height: '44px'
                }}
              />
            </Form.Group>
          </div>
          
          <div className="col-md-2">
            <Form.Group>
              <Form.Label className="text-muted small mb-1">Línea</Form.Label>
              <Form.Control
                type="text"
                placeholder="Ingrese línea"
                value={p_linea}
                onChange={(e) => setP_linea(e.target.value)}
                style={{
                  backgroundColor: '#f4f4f4',
                  borderRadius: '12px',
                  border: 'none',
                  height: '44px'
                }}
              />
            </Form.Group>
          </div>
          <div className="col-md-2 d-flex justify-content-end">
            <button
              className="ms-auto smu-generic-button solid-btn py-1"
              onClick={handleFilter}
              disabled={loading}
              style={{ height: '44px', width: '100px' }}
            >
              Filtrar
            </button>
          </div>


          <div className="col-md-2">
            {/* Visualizar como tienda */}
            {!isAdmin && loggedUser && loggedUser.store && loggedUser.store.length > 1 && (
              <Form.Group>
                <Form.Label className="text-muted small mb-1">Visualizar como tienda</Form.Label>
                <Form.Select
                  value={visualizarTienda ?? ""}
                  onChange={(e) => setVisualizarTienda(e.target.value)}
                  style={{
                    backgroundColor: '#f4f4f4',
                    borderRadius: '12px',
                    border: 'none',
                    height: '44px'
                  }}
                >
                  <option value="">Seleccionar tienda</option>
                  {loggedUser.store.map((store) => (
                    <option key={store} value={store}>
                      {parseInt(store).toString().padStart(4, '0')}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            {isAdmin && (
              <Form.Group>
                <Form.Label className="text-muted small mb-1">Visualizar como tienda</Form.Label>
                <div className="position-relative">
                  <Form.Control
                    type="text"
                    className="text-dark"
                    placeholder={getAdminSelectedStoreDisplay()}
                    value={isAdminStoreDropdownOpen ? adminStoreSearch : getAdminSelectedStoreDisplay()}
                    onChange={(e) => {
                      setAdminStoreSearch(e.target.value);
                      setIsAdminStoreDropdownOpen(true);
                    }}
                    onFocus={() => setIsAdminStoreDropdownOpen(true)}
                    onBlur={(e) => {
                      if (adminDropdownRef.current && adminDropdownRef.current.contains(e.relatedTarget)) {
                        return;
                      }
                      setTimeout(() => setIsAdminStoreDropdownOpen(false), 200);
                    }}
                    style={{
                      backgroundColor: '#f4f4f4',
                      borderRadius: '12px',
                      border: 'none',
                      height: '44px'
                    }}
                    autoComplete="off"
                    ref={adminSelectRef}
                  />
                  {isAdminStoreDropdownOpen && (
                    <div
                      ref={adminDropdownRef}
                      className="position-absolute w-100 bg-white border border-secondary rounded mt-1"
                      style={{ zIndex: 1000, maxHeight: '240px', overflowY: 'auto' }}
                      onMouseDown={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const scrollbarWidth = 17;
                        const isClickingOnScrollbar = e.clientX > rect.right - scrollbarWidth;
                        if (isClickingOnScrollbar) {
                          e.preventDefault();
                          e.stopPropagation();
                        }
                      }}
                    >
                      <div
                        className="px-3 py-2 cursor-pointer border-bottom"
                        style={{ cursor: 'pointer' }}
                        onMouseDown={() => {
                          setVisualizarTienda(null);
                          setAdminStoreSearch('');
                          setIsAdminStoreDropdownOpen(false);
                        }}
                      >
                        Todas
                      </div>
                      {(Array.isArray(filteredStores) ? filteredStores : []).map((store) => {
                        const code = String(store?.codLocal ?? store?.codigoLocal ?? store?.codigo ?? store?.id ?? "");
                        const label = /^\d+$/.test(code) ? parseInt(code, 10).toString().padStart(4, '0') : code;
                        return (
                          <div
                            key={code}
                            className="px-3 py-2 cursor-pointer border-bottom"
                            style={{ cursor: 'pointer' }}
                            onMouseDown={() => {
                              setVisualizarTienda(code);
                              setAdminStoreSearch('');
                              setIsAdminStoreDropdownOpen(false);
                            }}
                          >
                            {label}
                          </div>
                        );
                      })}
                      {filteredStores.length === 0 && adminStoreSearch && (
                        <div className="px-3 py-2 text-muted">No se encontraron tiendas</div>
                      )}
                    </div>
                  )}
                </div>
              </Form.Group>
            )}
          </div>
            </>
          )}
          
          
        </div>

        <div className="d-flex align-items-center mt-3 me-5">
          <div className="ms-auto d-flex align-items-center gap-4">
            <Form.Group className="mb-0">
              <Form.Check
                type="checkbox"
                id="onlyUpdated"
                label="Actualizaciones"
                checked={onlyUpdated}
                onChange={(e) => setOnlyUpdated(e.target.checked)}
              />
            </Form.Group>

            <button
              className="btn btn-primary btn-sm border-0"
              style={{ backgroundColor: COLOR_PRIMARY, color: COLOR_TEXT }}
              disabled={!selectedPromotions.length || !filteredPromotions.length}
              onClick={() => download()}
            >
              <span className="me-2">Excel</span> <IconDownload color={COLOR_TEXT} />
            </button>

            <Form.Check
              type="checkbox"
              id="checkbox-all"
              onChange={handleAddAll}
              disabled={!filteredPromotions.length}
            />
          </div>
        </div>
        
      </Card.Header>
      <Card.Body>
        <SmuTable
          columns={columns}
          data={filteredPromotions}
          rowsStyle={rowsStyle}
          defaultSortFieldId="FECHA_INICIO"
          defaultSortAsc={false}
          loading={loading}
        />
      </Card.Body>

      {downloading && (
        <span
          className="text-muted small"
          style={{
            position: "fixed",
            borderRadius: "8px",
            padding: "8px 16px",
            bottom: 16,
            right: 24,
            zIndex: 9999,
            background: "rgba(255,255,255,0.95)",
            boxShadow: "0 2px 8px rgba(0,0,0,0.38)",
          }}
        >
          {loadingPercent <= 40
            ? "1/2 Filtrando artículos de faldón..."
            : "2/2 Generando faldón PDF..."}
        </span>
      )}
    </Card>
  );
};

export default PromotionsTable;
